import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, CameraOff } from "lucide-react";
import { Button } from "./ui/button";

export const CameraCapture = ({ onCapture, busy, busyLabel = "Memproses…", label = "Ambil Foto", testid = "camera" }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Browser tidak mendukung kamera. Gunakan Chrome atau Safari terbaru.");
        return;
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false });
        if (cancelled) return s.getTracks().forEach((t) => t.stop());
        streamRef.current = s;
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
      } catch (e) {
        setError(e?.name === "NotAllowedError" ? "Izin kamera ditolak. Izinkan akses kamera di pengaturan browser lalu muat ulang halaman." : "Kamera tidak dapat dibuka.");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capture = () => {
    const v = videoRef.current;
    const scale = Math.min(1, 640 / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    onCapture(c);
  };

  if (error)
    return (
      <div data-testid={`${testid}-error`} className="flex flex-col items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
        <CameraOff className="h-8 w-8" />{error}
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-3xl bg-gray-900">
        <video ref={videoRef} data-testid={`${testid}-video`} playsInline muted autoPlay onLoadedMetadata={() => setReady(true)} className="h-full w-full -scale-x-100 object-cover" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[72%] w-[58%] rounded-[50%] border-4 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
        {!ready && <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Membuka kamera…</div>}
      </div>
      <p className="text-center text-xs text-gray-500">Posisikan wajah di dalam oval, pastikan cahaya cukup.</p>
      <Button data-testid={`${testid}-capture-btn`} disabled={!ready || busy} onClick={capture} className="h-14 w-full rounded-full text-base">
        {busy ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{busyLabel}</> : <><Camera className="mr-2 h-5 w-5" />{label}</>}
      </Button>
    </div>
  );
};
